import type { ReactNode } from 'react';
import React from 'react';

import type { RequireOnlyOne } from 'v2/interfaces';

interface IProps<T> {
  children: ReactNode;
  value: T;
  compare: RequireOnlyOne<ICompare<T>>;
}

interface ICompare<T> {
  eq?: Partial<T> | Partial<T>[];
  diff?: Partial<T> | Partial<T>[];
  gt?: Partial<T> | Partial<T>[];
  lt?: Partial<T> | Partial<T>[];
}

function compareEntries<T>(value: T, compare: Partial<T> | Partial<T>[]) {
  try {
    return Object.entries(compare).map(([k]) => [k, value[k]]);
  } catch (err) {
    return [];
  }
}

function createShowIf<T>() {
  const ShowIf: React.FC<IProps<T>> = ({ children, value, compare }) => {
    const type = Array.isArray(value) ? 'array' : typeof value;

    if (type === 'object') {
      if (compare.eq && !Array.isArray(compare.eq)) {
        return compareEntries<T>(value, compare.eq).every(([k, v]) => compare.eq[k] === v) ? <>{children}</> : null;
      }

      if (compare.diff && !Array.isArray(compare.diff)) {
        return compareEntries<T>(value, compare.diff).every(([k, v]) => compare.diff[k] !== v) ? <>{children}</> : null;
      }

      if (compare.gt && !Array.isArray(compare.gt)) {
        return compareEntries<T>(value, compare.gt).every(([k, v]) => compare.gt[k] > v) ? <>{children}</> : null;
      }

      if (compare.lt && !Array.isArray(compare.lt)) {
        return compareEntries<T>(value, compare.lt).every(([k, v]) => compare.lt[k] < v) ? <>{children}</> : null;
      }

      if (compare.eq && Array.isArray(compare.eq)) {
        const check = compare.eq.map((eq) => compareEntries<T>(value, eq).every(([k, v]) => eq[k] === v));

        return check.some((item: any) => item === true) ? <>{children}</> : null;
      }

      if (compare.diff && Array.isArray(compare.diff)) {
        const check = compare.diff.map((diff) => compareEntries<T>(value, diff).every(([k, v]) => diff[k] !== v));

        return check.some((item: any) => item === true) ? <>{children}</> : null;
      }

      if (compare.gt && Array.isArray(compare.gt)) {
        const check = compare.gt.map((gt) => compareEntries<T>(value, gt).every(([k, v]) => gt[k] > v));

        return check.some((item: any) => item === true) ? <>{children}</> : null;
      }

      if (compare.lt && Array.isArray(compare.lt)) {
        const check = compare.lt.map((lt) => compareEntries<T>(value, lt).every(([k, v]) => lt[k] < v));

        return check.some((item: any) => item === true) ? <>{children}</> : null;
      }

      return null;
    }

    if (compare.eq && Array.isArray(compare.eq)) {
      return !!compare.eq.some((eq: any) => eq === value) ? <>{children}</> : null;
    }

    if (compare.diff && Array.isArray(compare.diff)) {
      return !!compare.diff.some((diff: any) => diff !== value) ? <>{children}</> : null;
    }

    if (compare.gt && Array.isArray(compare.gt)) {
      return !!compare.gt.some((gt: any) => gt > value) ? <>{children}</> : null;
    }

    if (compare.lt && Array.isArray(compare.lt)) {
      return !!compare.lt.some((lt: any) => lt < value) ? <>{children}</> : null;
    }

    if (compare.eq || (typeof compare.eq === 'boolean' && compare.eq === false)) {
      return compare.eq === value ? <>{children}</> : null;
    }

    if (compare.diff || (typeof compare.diff === 'boolean' && compare.diff === false)) {
      return compare.diff === value ? <>{children}</> : null;
    }

    if (compare.gt || (typeof compare.gt === 'boolean' && compare.gt === false)) {
      return compare.gt === value ? <>{children}</> : null;
    }

    if (compare.lt || (typeof compare.lt === 'boolean' && compare.lt === false)) {
      return compare.lt === value ? <>{children}</> : null;
    }

    return null;
  };

  return ShowIf;
}

export default createShowIf;
